import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachIcon from '@mui/icons-material/AttachFileRounded';
import SendIcon from '@mui/icons-material/ArrowUpwardRounded';
import AddIcon from '@mui/icons-material/AddCommentOutlined';
import HistoryIcon from '@mui/icons-material/ForumOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import TextIcon from '@mui/icons-material/NotesRounded';
import PaletteIcon from '@mui/icons-material/PaletteOutlined';
import { useI18n, useTranslation } from '../i18n';
import { format } from '../i18n/I18nProvider';
import { getFingerprintNow } from '../hooks/useFingerprint';
import {
  cancelProposal,
  confirmProposal,
  createConversation,
  deleteConversation,
  getConversation,
  kindOfFile,
  MAX_ATTACHMENTS,
  listConversations,
  newClientId,
  retryTurn,
  sendMessage,
  uploadAttachment,
  type SendBody,
  type AgentOption,
  type Attachment,
  type AttachmentKind,
  type Conversation,
  type JobSnapshot,
  type StoredMessage,
} from '../lib/agent';
import { AgentThread } from '../components/agent/AgentThread';
import { AgentHistoryPanel } from '../components/agent/AgentHistoryPanel';
import { AgentErrorNotice } from '../components/agent/AgentErrorNotice';
import type { AgentErrorInfo } from '../lib/agentErrors';
import { agentJobs, isActive, newestJob, useAgentJobs } from '../lib/agentJobs';

const ACCEPT: Record<AttachmentKind, string> = { image: 'image/*', audio: 'audio/*', video: 'video/*' };

type Draft = { text: string; reasoning: string; status?: string };

// Hand the chat's submitted jobs to the app-wide tracker, seeded with the
// status the server returned alongside the conversation.
function trackJobs(conversationId: string, title: string | undefined, messages: StoredMessage[], jobs?: Record<string, JobSnapshot>) {
  for (const m of messages) {
    if (m.role !== 'event' || m.content.type !== 'proposal_confirmed') continue;
    const { activity_id, kind } = m.content;
    agentJobs.track({ activityId: activity_id, kind, conversationId, title }, jobs?.[activity_id]);
  }
}

export default function VideoAgent() {
  const t = useTranslation().videoAgent;
  const { locale } = useI18n();
  const [params, setParams] = useSearchParams();
  const convId = params.get('c');

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [messages, setMessages] = useState<StoredMessage[]>([]);
  // The open conversation is an API run: shown without the composer.
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A turn that failed without a persisted turn_failed row (HTTP error,
  // dropped stream): shown below the thread with Retry.
  const [turnError, setTurnError] = useState<AgentErrorInfo | null>(null);
  // The last message sent from this page, re-sent as-is (same client_id)
  // by Retry, which the server treats as a retry rather than a new message.
  const lastSend = useRef<{ convId: string; body: SendBody } | null>(null);

  const [text, setText] = useState('');
  const [placeholder, setPlaceholder] = useState<string | null>(null);
  const [pending, setPending] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(0);
  // Below lg the history panel moves into a drawer behind the header's Chats button.
  const [historyOpen, setHistoryOpen] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // What to do with the next picked file: attach to the composer, or send
  // straight away as the answer to an option chip.
  const pickTarget = useRef<{ kind?: AttachmentKind; send?: { label: string; callId: string; index: number } } | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  // The conversation messages are being sent to. Set synchronously when
  // send() creates one, before the URL (and convId) catch up.
  const liveConv = useRef<string | null>(convId);
  // Created by this page instance: its messages arrive over the stream, so
  // the URL change must not trigger a (racing) reload.
  const createdHere = useRef<string | null>(null);

  const refreshList = useCallback(() => {
    listConversations()
      .then(setConversations)
      .catch(() => {})
      .finally(() => setListLoaded(true));
  }, []);
  useEffect(refreshList, [refreshList]);

  useEffect(() => {
    if (convId && convId === createdHere.current) return;
    liveConv.current = convId;
    setMessages([]);
    setReadOnly(false);
    setError(null);
    setTurnError(null);
    if (!convId) return;
    setLoading(true);
    getConversation(convId)
      .then((r) => {
        setMessages(r.messages);
        setReadOnly(!!r.conversation.read_only);
        trackJobs(convId, r.conversation.title, r.messages, r.jobs);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [convId]);

  // Landing on a bare /agent (nav link, reload of a new chat) while a video
  // is still rendering: reopen that chat rather than an empty one. Only on
  // arrival, never after the user started something here.
  const jobs = useAgentJobs();
  const restoreChecked = useRef(!!convId);
  useEffect(() => {
    if (restoreChecked.current) return;
    void agentJobs.bootstrap().then(() => {
      if (restoreChecked.current) return;
      restoreChecked.current = true;
      if (liveConv.current || busy) return;
      const newest = newestJob(agentJobs.all().filter((j) => isActive(j) && j.conversationId));
      if (newest?.conversationId) setParams({ c: newest.conversationId }, { replace: true });
    });
  }, [busy, setParams]);
  const runningConvs = new Set(jobs.filter((j) => isActive(j) && j.conversationId).map((j) => j.conversationId));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, draft?.text, busy]);

  const upsert = (m: StoredMessage) =>
    setMessages((prev) => (prev.some((x) => x.id === m.id && x.seq === m.seq) ? prev : [...prev, m]));

  const reload = useCallback(async (id: string) => {
    const r = await getConversation(id);
    if (liveConv.current !== id) return;
    setMessages(r.messages);
    trackJobs(id, r.conversation.title, r.messages, r.jobs);
  }, []);

  // Runs one agent turn: a new (or re-sent) message when `body` is given,
  // otherwise the server-side retry of the conversation's last turn.
  const runTurn = useCallback(
    async (body?: SendBody) => {
      if (busy) return;
      restoreChecked.current = true;
      setBusy(true);
      setError(null);
      setTurnError(null);
      setDraft({ text: '', reasoning: '' });
      let id = liveConv.current;
      // The server persisted this failure; the thread shows it with Retry.
      let failureRow = false;
      try {
        if (!id) {
          id = (await createConversation()).id;
          liveConv.current = id;
          createdHere.current = id;
          setParams({ c: id }, { replace: false });
        }
        const onEvent: Parameters<typeof sendMessage>[2] = (e) => {
          if (e.type === 'text') setDraft((d) => ({ text: (d?.text ?? '') + e.delta, reasoning: d?.reasoning ?? '' }));
          else if (e.type === 'reasoning') setDraft((d) => ({ text: d?.text ?? '', reasoning: (d?.reasoning ?? '') + e.delta }));
          else if (e.type === 'message') {
            upsert(e.message);
            // The server titles a new chat from its first message.
            if (e.message.role === 'user' && id === createdHere.current) refreshList();
            if (e.message.role === 'event' && e.message.content.type === 'turn_failed') failureRow = true;
            // A persisted assistant message replaces the streamed draft; a
            // new draft starts if the loop continues after tool calls.
            if (e.message.role !== 'user') setDraft({ text: '', reasoning: '' });
          } else if (e.type === 'tool_start' && e.name === 'make_reference_sheet') {
            setDraft((d) => ({ text: d?.text ?? '', reasoning: d?.reasoning ?? '', status: t.makingSheet }));
          } else if (e.type === 'tool_start' && e.name === 'propose_video') {
            // The server writes the final H3 prompt before the card appears.
            setDraft((d) => ({ text: d?.text ?? '', reasoning: d?.reasoning ?? '', status: t.writingPrompt }));
          } else if (e.type === 'error' && !failureRow) setTurnError({ raw: e.error });
        };
        if (body) {
          lastSend.current = { convId: id, body };
          await sendMessage(id, body, onEvent);
        } else {
          await retryTurn(id, onEvent);
        }
      } catch (e) {
        const err = e as Error & { status?: number; body?: { error?: string } };
        const code = err.body?.error;
        if ((code === 'already_answered' || code === 'nothing_to_retry') && id) {
          // The turn finished after all (e.g. only the stream dropped): show it.
          await reload(id).catch(() => {});
        } else {
          setTurnError({ raw: err.message, status: err.status, code });
        }
      } finally {
        setDraft(null);
        setBusy(false);
        refreshList();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy, refreshList, reload, setParams, t.writingPrompt, t.makingSheet]
  );

  const send = useCallback(
    (body: { text: string; attachments?: Attachment[]; choice?: { call_id: string; option_id: string } }) =>
      runTurn({ ...body, locale, client_id: newClientId() }),
    [runTurn, locale]
  );

  // Retry the failed turn: re-send this page's last message (same client_id,
  // so it is never stored twice), or after a reload ask the server to re-run
  // the conversation's last turn.
  const retry = () => {
    const last = lastSend.current;
    void runTurn(last && last.convId === liveConv.current ? last.body : undefined);
  };

  const submitComposer = () => {
    const body = text.trim();
    if ((!body && !pending.length) || busy || uploading) return;
    void send({ text: body, attachments: pending.length ? pending : undefined });
    setText('');
    setPending([]);
    setPlaceholder(null);
  };

  const openPicker = (kind?: AttachmentKind, sendAs?: { label: string; callId: string; index: number }) => {
    pickTarget.current = { kind, send: sendAs };
    if (fileRef.current) {
      fileRef.current.accept = kind ? ACCEPT[kind] : 'image/*,audio/*,video/*';
      fileRef.current.value = '';
      fileRef.current.click();
    }
  };

  const onFiles = async (files: FileList | null) => {
    const target = pickTarget.current;
    pickTarget.current = null;
    if (!files?.length) return;
    // Picked files go into the composer (or straight out) up to the per-message cap.
    const room = target?.send ? MAX_ATTACHMENTS : MAX_ATTACHMENTS - pending.length - uploading;
    const list = Array.from(files).slice(0, Math.max(0, room));
    if (files.length > list.length) setError(format(t.tooManyFiles, { max: MAX_ATTACHMENTS }));
    if (!list.length) return;
    setUploading((n) => n + list.length);
    const done: Attachment[] = [];
    try {
      for (const f of list) {
        const kind = kindOfFile(f) ?? target?.kind;
        done.push(await uploadAttachment(f, kind ?? undefined));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading((n) => n - list.length);
    }
    if (!done.length) return;
    if (target?.send) {
      void send({ text: target.send.label, attachments: done, choice: { call_id: target.send.callId, option_id: String(target.send.index) } });
    } else {
      setPending((p) => [...p, ...done]);
    }
  };

  const onPick = (callId: string, o: AgentOption, index: number) => {
    const action = o.action ?? 'reply';
    if (action === 'upload_image' || action === 'upload_audio' || action === 'upload_video') {
      openPicker(action.slice('upload_'.length) as AttachmentKind, { label: o.label, callId, index });
      return;
    }
    if (action === 'type') {
      setPlaceholder(o.placeholder || o.label);
      inputRef.current?.focus();
      return;
    }
    void send({ text: o.label, choice: { call_id: callId, option_id: String(index) } });
  };

  const onConfirm = async (callId: string, edits: { prompt?: string; duration?: number; tier?: string }) => {
    if (!liveConv.current) return;
    const fingerprint = await getFingerprintNow().catch(() => undefined);
    try {
      const conversationId = liveConv.current;
      const event = await confirmProposal(conversationId, callId, { ...edits, fingerprint });
      upsert(event);
      trackJobs(conversationId, conversations.find((c) => c.id === conversationId)?.title, [event]);
    } catch (e) {
      const err = e as { row?: StoredMessage; code?: string };
      if (err.row) upsert(err.row);
      // The content check refused it just now: hand it straight back to the
      // agent to revise (a resubmit of the same plan would be refused again).
      if (err.code === 'content_rejected' && err.row) onRevise();
      // Already submitted (e.g. from another tab): show the real state.
      if (err.code === 'already_settled' && liveConv.current) return reload(liveConv.current).catch(() => {});
      throw e;
    }
  };

  const onRevise = () => {
    if (!busy) void send({ text: t.reviseRejected });
  };

  const onCancel = async (callId: string) => {
    if (!liveConv.current) return;
    upsert(await cancelProposal(liveConv.current, callId));
  };

  const onUseImage = (a: Attachment, action: 'animate' | 'edit') => {
    void send({ text: action === 'animate' ? t.animate : t.editImage, attachments: [a] });
  };

  const newChat = () => {
    restoreChecked.current = true;
    liveConv.current = null;
    createdHere.current = null;
    setMessages([]);
    setParams({});
  };

  const empty = !convId && !messages.length;

  const openChat = (id: string) => {
    setHistoryOpen(false);
    if (id !== convId) setParams({ c: id });
  };

  const removeChat = async (id: string) => {
    await deleteConversation(id).catch(() => {});
    if (id === convId) newChat();
    refreshList();
  };

  const historyProps = {
    conversations,
    loaded: listLoaded,
    activeId: convId,
    runningIds: runningConvs,
    onOpen: openChat,
    onNew: () => {
      setHistoryOpen(false);
      newChat();
    },
    onDelete: (id: string) => void removeChat(id),
    newDisabled: busy || empty,
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: -6 }}>
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 860, mx: 'auto', minHeight: 'calc(100vh - 72px)', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ flex: 1 }}>
            {t.title}
          </Typography>
          <Box sx={{ display: { xs: 'flex', lg: 'none' }, gap: 1 }}>
            <Tooltip title={t.chats}>
              <IconButton onClick={() => setHistoryOpen(true)} aria-label={t.chats}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t.newChat}>
              <span>
                <IconButton onClick={newChat} disabled={busy || empty} aria-label={t.newChat}>
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Stack>

        <Box sx={{ flex: 1, pb: 3 }}>
          {loading && <CircularProgress size={22} />}
          {empty && !busy && (
            <Stack spacing={2.5} sx={{ pt: { xs: 1, md: 4 } }}>
              <Typography sx={{ fontSize: 15.5, lineHeight: 1.65, maxWidth: 640 }}>{t.intro}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <StarterCard icon={<ImageIcon />} title={t.startFromImage} hint={t.startFromImageHint} onClick={() => void send({ text: t.startFromImage })} />
                <StarterCard icon={<TextIcon />} title={t.startFromText} hint={t.startFromTextHint} onClick={() => void send({ text: t.startFromText })} />
                <StarterCard icon={<PaletteIcon />} title={t.startImagesFirst} hint={t.startImagesFirstHint} onClick={() => void send({ text: t.startImagesFirst })} />
              </Stack>
            </Stack>
          )}
          <AgentThread
            messages={messages}
            busy={busy || readOnly}
            draft={draft}
            handlers={{ onPick, onConfirm, onCancel, onUseImage, onRetryTurn: retry, onRevise: readOnly ? undefined : onRevise }}
          />
          {turnError && (
            <Box sx={{ mt: 2 }}>
              <AgentErrorNotice context="turn" error={turnError} onRetry={retry} retrying={busy} onClose={() => setTurnError(null)} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
              {format(t.error, { error })}
            </Alert>
          )}
          <div ref={bottomRef} />
        </Box>

        <Box sx={{ position: 'sticky', bottom: 0, pb: 2, pt: 1, bgcolor: 'background.default' }}>
          {readOnly && (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 3 }}>
              {t.apiRunReadOnly}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ display: readOnly ? 'none' : undefined, borderRadius: 3, px: 1.25, py: 1, '&:focus-within': { borderColor: 'primary.main' } }}>
            {(pending.length > 0 || uploading > 0) && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 0.5, pb: 1 }}>
                {pending.map((a) => (
                  <Chip
                    key={a.r2_key}
                    size="small"
                    label={a.name ?? a.kind}
                    avatar={a.kind === 'image' ? <Box component="img" src={a.url} alt="" sx={{ objectFit: 'cover' }} /> : undefined}
                    onDelete={() => setPending((p) => p.filter((x) => x !== a))}
                  />
                ))}
                {uploading > 0 && <Chip size="small" icon={<CircularProgress size={12} />} label={t.uploading} />}
              </Stack>
            )}
            <Stack direction="row" spacing={0.5} alignItems="flex-end">
              <Tooltip title={t.attach}>
                <IconButton onClick={() => openPicker()} disabled={busy} aria-label={t.attach}>
                  <AttachIcon />
                </IconButton>
              </Tooltip>
              <TextField
                inputRef={inputRef}
                multiline
                maxRows={10}
                fullWidth
                variant="standard"
                placeholder={placeholder ?? t.placeholder}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submitComposer();
                  }
                }}
                InputProps={{ disableUnderline: true, sx: { py: 1, fontSize: 14.5 } }}
              />
              <IconButton
                color="primary"
                onClick={submitComposer}
                disabled={busy || uploading > 0 || (!text.trim() && !pending.length)}
                aria-label={t.send}
                sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.selected' } }}
              >
                {busy ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              </IconButton>
            </Stack>
          </Paper>
          <input ref={fileRef} type="file" hidden multiple onChange={(e) => void onFiles(e.target.files)} />
        </Box>
      </Box>
      <AgentHistoryPanel {...historyProps} sx={{ display: { xs: 'none', lg: 'flex' } }} />
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)} sx={{ display: { lg: 'none' } }}>
        <AgentHistoryPanel {...historyProps} sticky={false} width={300} sx={{ height: '100%', maxWidth: '85vw', border: 0, borderRadius: 0 }} />
      </Drawer>
    </Box>
  );
}

function StarterCard({ icon, title, hint, onClick }: { icon: ReactNode; title: string; hint: string; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: 1,
        textAlign: 'left',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'border-color .15s, transform .15s',
        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-1px)' },
      }}
    >
      <Stack spacing={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            color: '#b9b0ff',
            bgcolor: 'rgba(124, 111, 240, 0.14)',
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{title}</Typography>
        <Typography variant="body2">{hint}</Typography>
      </Stack>
    </ButtonBase>
  );
}
